import type { ReactElement } from "react";

import type { IconName } from "../../components/ui/icon";
import type { DataTableGroupSortDirection } from "../../hooks/use-data-table-state";
import type { Omittable } from "../../lib/props";
import {
  FieldDirectionDropdown,
  type FieldDropdownCopy,
} from "./data-table-field-dropdown";

export type DataTableGroupByDropdownProps = Omittable<{
  groupByField: string;
  setGroupByField: (field: string) => void;
  groupByLabels: Record<string, string>;
  groupByIcons?: Partial<Record<string, IconName>>;
  groupSortDirection?: DataTableGroupSortDirection;
  setGroupSortDirection?: (direction: DataTableGroupSortDirection) => void;
  /** The surface cannot render ungrouped — a board's columns ARE its grouping —
   * so the "—" clear row is not offered at all. */
  requireGrouping?: boolean;
  /** Caller-owned trigger element replacing the default toolbar button — a
   * settings row that presses across its whole width, say. It receives the
   * trigger's own onClick / aria-expanded / data-popup-open / ref, so it has to
   * forward unknown props to the element it renders. A trigger that already
   * names the active field carries no tooltip: the tooltip exists to answer
   * "grouped by what?" for the compact button face. */
  trigger?: ReactElement<Record<string, unknown>>;
  /** Suppress the active-field label on the trigger and show only the icon —
   * the same collapsed look the toggle takes on at narrow/mobile widths —
   * even when there's room for the label. Use where the toolbar should stay
   * compact (e.g. a narrow drawer). */
  iconOnly?: boolean;
}>;

const COPY: FieldDropdownCopy = {
  title: "Group by",
  tooltipVerb: "Grouping by",
  directionLabel: "Group order",
  icon: "layers",
};

export function DataTableGroupByDropdown({
  groupByField,
  setGroupByField,
  groupByLabels,
  groupByIcons,
  groupSortDirection,
  setGroupSortDirection,
  trigger,
  iconOnly,
  requireGrouping,
}: DataTableGroupByDropdownProps) {
  return (
    <FieldDirectionDropdown
      field={groupByField}
      setField={setGroupByField}
      labels={groupByLabels}
      icons={groupByIcons}
      direction={groupSortDirection}
      setDirection={setGroupSortDirection}
      requireField={requireGrouping}
      trigger={trigger}
      iconOnly={iconOnly}
      copy={COPY}
    />
  );
}
